import {Injectable, PLATFORM_ID, inject, signal} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router} from '@angular/router';

/**
 * Loader global : visible pendant une navigation et tant que les requêtes HTTP
 * lancées pendant ce chargement de page ne sont pas terminées.
 * Les requêtes lancées ensuite (polling, actions utilisateur) ne l'affichent pas.
 */
@Injectable({providedIn: 'root'})
export class LoadingService {

	private static readonly SHOW_DELAY_MS = 150;
	private static readonly MAX_DURATION_MS = 15000;

	private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

	private phaseActive = false;
	private navigating = false;
	private pending = 0;
	private showTimer: ReturnType<typeof setTimeout> | null = null;
	private maxTimer: ReturnType<typeof setTimeout> | null = null;

	private readonly _visible = signal(false);
	readonly visible = this._visible.asReadonly();

	constructor() {
		if (!this.isBrowser) return;

		inject(Router).events.subscribe(event => {
			if (event instanceof NavigationStart) {
				this.navigating = true;
				this.startPhase();
			} else if (
				event instanceof NavigationEnd ||
				event instanceof NavigationCancel ||
				event instanceof NavigationError
			) {
				this.navigating = false;
				this.endPhaseIfIdle();
			}
		});
	}

	/** Appelé par l'interceptor. Retourne true si la requête est suivie. */
	requestStarted(): boolean {
		if (!this.isBrowser || !this.phaseActive) return false;
		this.pending++;
		return true;
	}

	requestFinished(): void {
		this.pending = Math.max(0, this.pending - 1);
		this.endPhaseIfIdle();
	}

	private startPhase(): void {
		this.phaseActive = true;
		if (!this.showTimer && !this._visible()) {
			this.showTimer = setTimeout(() => {
				this.showTimer = null;
				if (this.phaseActive) this._visible.set(true);
			}, LoadingService.SHOW_DELAY_MS);
		}
		if (!this.maxTimer) {
			this.maxTimer = setTimeout(() => this.reset(), LoadingService.MAX_DURATION_MS);
		}
	}

	private endPhaseIfIdle(): void {
		if (this.phaseActive && !this.navigating && this.pending === 0) this.reset();
	}

	private reset(): void {
		this.phaseActive = false;
		this.navigating = false;
		this.pending = 0;
		if (this.showTimer) clearTimeout(this.showTimer);
		if (this.maxTimer) clearTimeout(this.maxTimer);
		this.showTimer = this.maxTimer = null;
		this._visible.set(false);
	}
}
